import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Users, MapPin, Search, Loader2, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Discover = () => {
  const [users, setUsers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [minAge, setMinAge] = useState('');
  const [maxAge, setMaxAge] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [userPersona, setUserPersona] = useState('');
  const [viewMode, setViewMode] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const persona = user.persona || '';
    setUserPersona(persona);
    
    // Set default view mode based on user persona
    // Athletes should see professionals by default (they can't discover other athletes)
    // Professionals and clubs should see athletes by default
    if (persona.toLowerCase() === 'athlete') {
      setViewMode('professionals');
    } else if (['club', 'coach', 'physio', 'nutritionist'].includes(persona.toLowerCase())) {
      setViewMode('athletes');
    } else {
      // For fan or others, default to professionals
      setViewMode('professionals');
    }
  }, []);

  useEffect(() => {
    if (viewMode) {
      loadUsers();
      loadSuggestions();
    }
  }, [viewMode, sportFilter, locationFilter, positionFilter, minAge, maxAge]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {};
      if (sportFilter) params.sport = sportFilter;
      if (locationFilter) params.location = locationFilter;
      if (positionFilter) params.position = positionFilter;
      if (minAge) params.minAge = parseInt(minAge);
      if (maxAge) params.maxAge = parseInt(maxAge);

      let endpoint = '/api/discovery/athletes';
      if (viewMode === 'professionals') endpoint = '/api/discovery/professionals';
      if (viewMode === 'clubs') endpoint = '/api/discovery/clubs';

      const response = await axios.get(`${BACKEND_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      const key = viewMode === 'athletes' ? 'athletes' : viewMode === 'professionals' ? 'professionals' : 'clubs';
      setUsers(response.data[key] || []);
    } catch (error) {
      console.error('Error loading users:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Show more specific error message
      const errorMessage = error.response?.data?.detail || 'Failed to load profiles';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/discovery/suggestions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Error loading suggestions:', error);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 pt-20">
        <div className="py-4">
          {/* Header - Centered */}
          <div className="text-center mb-4">
            <h1 className="text-xl font-bold text-foreground">Discover</h1>
            <p className="text-muted-foreground text-sm mt-0.5">Find athletes, professionals, and clubs</p>
          </div>

          <div className="flex space-x-2 mb-4 justify-center">
            {/* Athletes can't view other athletes - hide this tab for them */}
            {userPersona.toLowerCase() !== 'athlete' && (
              <Button
                variant={viewMode === 'athletes' ? 'default' : 'outline'}
                onClick={() => setViewMode('athletes')}
                className={viewMode === 'athletes' ? 'btn-primary' : ''}
              >
                Athletes
              </Button>
            )}
            <Button
              variant={viewMode === 'professionals' ? 'default' : 'outline'}
              onClick={() => setViewMode('professionals')}
              className={viewMode === 'professionals' ? 'btn-primary' : ''}
            >
              Professionals
            </Button>
            <Button
              variant={viewMode === 'clubs' ? 'default' : 'outline'}
              onClick={() => setViewMode('clubs')}
              className={viewMode === 'clubs' ? 'btn-primary' : ''}
            >
              Clubs
            </Button>
          </div>

          <div className="mb-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative col-span-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Input
                placeholder="Sport..."
                value={sportFilter}
                onChange={(e) => setSportFilter(e.target.value)}
              />
              <Input
                placeholder="Location..."
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
              />
            </div>

            {/* Advanced Filters - Only for clubs, physios, nutritionists */}
            {viewMode === 'athletes' && ['club', 'coach', 'physio', 'nutritionist'].includes(userPersona) && (
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className="mb-3"
                >
                  {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
                </Button>

                {showAdvancedFilters && (
                  <Card>
                    <CardContent className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label>Position</Label>
                          <Input
                            placeholder="e.g., Striker, Forward"
                            value={positionFilter}
                            onChange={(e) => setPositionFilter(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Min Age</Label>
                          <Input
                            type="number"
                            placeholder="e.g., 18"
                            value={minAge}
                            onChange={(e) => setMinAge(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Max Age</Label>
                          <Input
                            type="number"
                            placeholder="e.g., 30"
                            value={maxAge}
                            onChange={(e) => setMaxAge(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setPositionFilter('');
                            setMinAge('');
                            setMaxAge('');
                          }}
                        >
                          Clear Filters
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>

          {suggestions.length > 0 && (
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <TrendingUp className="w-5 h-5 mr-2 text-primary" />
                  <h2 className="text-lg font-bold">Suggested For You</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {suggestions.slice(0, 4).map((user) => (
                    <div
                      key={user._id}
                      className="text-center cursor-pointer hover:opacity-80"
                      onClick={() => navigate(`/profile/${user.username}`)}
                    >
                      <Avatar className="w-16 h-16 mx-auto mb-2">
                        <AvatarImage src={user.profileImage} />
                        <AvatarFallback>{user.name[0]}</AvatarFallback>
                      </Avatar>
                      <p className="text-sm font-semibold truncate">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{user.persona}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="discover-skeleton">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <Skeleton className="w-16 h-16 rounded-full flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-28" />
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-5 w-16 rounded-full" />
                      </div>
                    </div>
                    <Skeleton className="h-3.5 w-full mt-3" />
                    <Skeleton className="h-3.5 w-2/3 mt-1.5" />
                    <Skeleton className="h-3.5 w-28 mt-3" />
                    <div className="flex gap-1.5 mt-3">
                      <Skeleton className="h-6 w-16 rounded" />
                      <Skeleton className="h-6 w-20 rounded" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredUsers.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-lg font-semibold text-foreground mb-2">No users found</p>
                <p className="text-muted-foreground">Try adjusting your filters</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 skeleton-fade-in">
              {filteredUsers.map((user) => (
                <Card
                  key={user._id}
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => navigate(`/profile/${user.username}`)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <Avatar className="w-16 h-16">
                        <AvatarImage src={user.profileImage} />
                        <AvatarFallback>{user.name[0]}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h3 className="font-bold text-foreground">{user.name}</h3>
                        <p className="text-sm text-muted-foreground">@{user.username}</p>
                        <Badge className="badge-primary">
                          {user.persona}
                        </Badge>
                      </div>
                    </div>
                    {user.bio && (
                      <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{user.bio}</p>
                    )}
                    {user.location && (
                      <div className="flex items-center text-sm text-muted-foreground mt-3">
                        <MapPin className="w-4 h-4 mr-1" />
                        {user.location}
                      </div>
                    )}
                    {user.sports && user.sports.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {user.sports.slice(0, 3).map((sport, i) => (
                          <span key={i} className="text-xs px-2 py-1 bg-secondary rounded">
                            {sport}
                          </span>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Discover;
